import React, { useState, useEffect, useRef } from 'react';
import { Dialog, AppBar, Toolbar, IconButton, Typography, Button, Icon, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles from './TaskDetail.styles';
import { useSelector } from 'react-redux';
import { get, size, first, set, last, sortBy, noop, snakeCase } from 'global/lib/lodash';
import Form from 'global/components/form';
import { useScreen, Loading } from 'global/styles';
import Alert from '@mui/material/Alert';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { setBodyOverflow } from 'global/hooks/useBodyOverflow';
import { getTaskStatus, uppercaseFirst, getUrlParams } from 'global/utils';
import { ImageList } from 'global/components/image';
import sendData from 'redux/actions/sendData';
import { RenderTag } from '../TaskCreate/TaskCreate';
import Confirm from 'global/components/confirm';
import clsx from 'clsx';
import { withRouter } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Styles, { Loader } from 'global/styles';
import TaskTag from '../TaskCreate/TaskTag';
import { UploadFiles } from 'redux/actions/imageUploader';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';
import { CommentContainer, allowedToEdit } from './comments';
import moment from 'moment';
import { useSocket } from '../../../global/hooks/useSocket';
import { fixWeirdDateIssue } from '../../../global/components/form';
import { taskOptions } from '../../admin/account-settings';
import { TaskScoreRef } from '../CreateModal';

export const TaskParams = {
  build: ['tasks'],
  transformData: (d) => {
    const v = first(d);

    const newmeta = {};

    for (let k in v.meta) {
      set(newmeta, snakeCase(k), v.meta[k]);
    }

    v.meta = newmeta;

    return v;
  },
  params: {
    include: [
      'images',
      'assignees',
      'viewers',
      'authors',
      'comments',
      'comments.author',
      'comments.images',
      'realm',
      'scorecardScore',
      'scorecardScore.checklist',
      'scorecardScore.checkpoint',
      'scorecardScore.checkpoint.checkpointOptions',
      'reportSection',
      'reportSection.images',
      'subScore',
      'subScore.scorecardScore',
      'tradeTypes',
    ].join(','),
  },
};

export const DetailModal = (props) => {
  const { open } = props;
  return !open ? null : <TaskDetail {...props} />;
};

const TaskDetail = withRouter((props) => {
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [formState, setFormState] = useState({});
  let [comments, setComments] = useState([]);
  const [realmId, setRealmId] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState();
  const [tag, setTag] = useState();
  const [tagName, setTagName] = useState();
  const [tagModal, setTagModal] = useState(false);

  const [id, setId] = useState(props.taskId || get(props, 'match.params.id'));
  const formRef = useRef();

  const classes = useStyles();
  const styles = Styles();
  const uhook = useUser();
  const { me } = uhook;
  const taskList = useSelector((state) => get(state, 'view.task.list'));
  const { isMobileXS } = useScreen();

  const loca = useLocation();
  const { tcid } = getUrlParams(get(loca, 'search'));

  const taskData = useRetrieveData({ url: `/tasks/${id}` }, TaskParams);
  const { data, loading, update } = taskData;
  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId }, useCache: { reload: 1 } },
    }
  );
  const tci = useRetrieveData(
    { url: `/processed-task-count-values` },
    {
      waitFor: !tcid && realmId,
      build: ['processedTaskCountValues'],
      params: { filter: { realmid: realmId } },
      transformData: (da) => get(da, '0', {}),
    }
  );
  const d = data;
  const authuser = get(d, 'authors.0', {});
  const status = getTaskStatus(d);
  const useHistory = get(props, 'useHistory', true);

  const scorecardId = get(d, 'scorecardScore.scorecardId') || get(d, 'subScore.scorecardScore.scorecardId');
  const scData = useRetrieveData(
    { url: `/scorecards/${scorecardId}` },
    {
      waitFor: !scorecardId ? null : undefined,
      build: ['scorecards'],
    }
  );
  const scorecard = get(scData, 'data.0');
  const scorecardConfig = get(scData, 'data.0.config');

  useEffect(() => {
    if (d) {
      setComments(get(d, 'comments', []));
      setRealmId(get(d, 'realm.id', null));

      if (get(d, 'scorecardScore')) {
        const scs = get(d, 'scorecardScore');
        const cp = get(scs, 'checkpoint');

        const _tag = {
          scorecardConfig: scorecardConfig,
          ...cp,
          ...scs,
          pssvCheckpoint: cp,
          pssvChecklist: get(scs, 'checklist'),
          scorecardScoreId: get(d, 'scorecardScoreId')
        };
        setTag(_tag);
      } else if (get(d, 'subScore')) {
        const { subScore, ...rest } = d;
        setTag({
          ...rest,
          scorecardConfig: scorecardConfig,
          ...get(subScore, 'scorecardScore', {}),
          ...subScore,
          pssvCheckpoint: subScore,
          pssvChecklist: get(subScore, 'checklist'),
          scorecardScoreId: get(d, 'subScore.scorecardScoreId')
        });
      } else if (get(d, 'reportSection')) {
        setTag({
          ...d,
        });
      } else {
        setTag();
      }
    }
  }, [d, scorecard]);

  useSocket(
    {
      channel: `task.${id}`,
      onEvent: (resp) => {
        const comment = get(resp, 'data', get(resp, 'comment', resp));

        setComments([...comments, comment]);
      },
    },
    [comments]
  );

  const toggleEdit = () => {
    setCanEdit(!canEdit);
  };

  const ate = allowedToEdit(d, comments, { user: uhook });

  const formatData = async (values) => {
    const relationships = {};
    const assignedTo = get(values, 'assignedTo');

    let tresult;
    if (values.tagAdded && tag) {
      const selectedOption = get(tag, 'pssvCheckpoint.selectedOption');
      const cpo = get(tag, `pssvCheckpoint.checkpointOptions.${get(selectedOption, 'index')}`);
      tresult = await sendData({
        url: '/scorecard-scores',
        method: 'post',
        data: {
          data: {
            type: 'scorecard-scores',
            attributes: {
              scorecard_id: get(tag, 'scorecardId'),
              checklist_id: get(tag, 'checklistId'),
              checkpoint_id: get(tag, 'checkpointId'),
              checkpoint_option_id: get(cpo, 'id'),
              criteria: get(tag, 'criteria'),
              score: get(cpo, 'value', 0),
            },
          },
        },
      });
    }

    const getStatus = () => {
      const createComment = last(comments);
      if (createComment) {
        const newStat = get(createComment, 'newStatus');
        if (newStat) {
          return newStat === 'pending' ? 1 : newStat === 'closed' ? 2 : 0;
        } else {
          return status.status;
        }
      }
    };

    const stat = getStatus();

    const attributes = {
      name: get(values, 'name'),
      description: get(values, 'description'),
      status: stat,
      images: get(values, 'images', get(d, 'images', [])),
      due_date: get(values, 'dueDate'),
      meta: get(values, 'meta'),
    };

    if (values.tagAdded) {
      set(attributes, 'scorecard_score_id', get(tresult, 'data.data.id', null));
    }

    if (assignedTo !== undefined) {
      set(relationships, 'assignees', {
        data: size(assignedTo) ? assignedTo.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
      });
    }

    const comms = comments.filter((comment) => comment.isNew);
    if (size(comms)) {
      await Promise.all(
        comms.map(async (comment) => {
          const _data = {
            data: {
              type: 'comments',
              attributes: {
                user_id: get(me, 'id'),
                ...comment,
                commentable_id: id,
                commentable_type: 'task',
                body: get(comment, 'body', ''),
                meta: {
                  showCorrection: stat !== status.status,
                  status: stat,
                },
              },
            },
          };

          await UploadFiles(_data, { attributeType: 'images' });

          return await sendData({
            url: `/comments`,
            method: 'post',
            data: _data,
          });
        })
      );
    }

    const __data = {
      data: {
        id,
        type: 'tasks',
        attributes,
        relationships,
      },
    };

    const tradeTypes = get(values, 'tradeTypes');
    if (size(tradeTypes)) {
      set(__data, 'data.relationships.tradeTypes', {
        data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
      });
    }

    const shouldUpdate = !!get(attributes, 'images', []).find((im) => im.hasChanged || im.id.indexOf('_file_') > -1);
    await UploadFiles(__data, { shouldUpdate });

    return __data;
  };

  const formSubmit = async (values) => {
    try {
      setFormLoading(true);
      const _data = await formatData(values);
      await sendData(
        {
          url: `/tasks/${id}`,
          method: 'patch',
          data: _data,
        },
        { simulate: false }
      );
      await update();
      setFormLoading(false);
      setCanEdit(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const formUpdate = (vals) => {
    for (let k in vals) {
      set(formState, k, vals[k]);
    }
    setFormState({ ...formState });
  };

  const handleClose = () => {
    setBodyOverflow();
    if (props.onClose) {
      return props.onClose(first(data));
    }
    if (!tcid && props.history.length > 2) {
      props.history.goBack();
      const location = get(props, 'history.location.pathname');
      setTimeout(() => {
        if (get(props, 'history.location.pathname') === location) {
          props.history.goBack();
        }
      }, 200);
    } else {
      const tciid = tcid ? tcid : get(tci, 'data.id');
      if (tciid) {
        const params = realmId ? `?realmId=${realmId}` : '';
        props.history.push(`/tasks/list/${tciid}${params}`);
      } else {
        props.history.goBack();
      }
    }
  };

  const onSubmitComment = async (comms) => {
    try {
      comments = comms;
      setComments(comms);
      await formSubmit(formState);
      update();
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    try {
      setFormLoading(true);
      await sendData({
        url: `/tasks/${id}`,
        method: 'patch',
        data: {
          data: {
            id,
            type: 'tasks',
            attributes: {
              archived: true,
            },
          },
        },
      });
      setFormLoading(false);
      handleClose();
    } catch (err) {
      setError(`Error: ${err.message}`);
      setFormLoading(false);
    }
  };

  const onDelete = () => {
    setDeleteConfirm({
      title: 'Delete Task',
      content: 'Are you sure you want to delete this task?',

      onCancel: () => setDeleteConfirm(),
      onOk: () => {
        confirmDelete();
        setDeleteConfirm();
      },
    });
  };

  useEffect(() => {
    if (!useHistory) {
      setDetailParam();
    }
  }, [id]);

  const setDetailParam = (_id = id) => {
    const search = new URLSearchParams(loca.search);
    search.set('detail', _id);
    props.history.replace(`${loca.pathname}?${search.toString()}`);
  };

  const nextListItem = (dir = 'right') => {
    const i = taskList.findIndex((t) => t.id === id);
    const nextItem = (() => {
      if (dir === 'right') {
        return taskList[i + 1] ? taskList[i + 1] : first(taskList);
      } else {
        return taskList[i - 1] ? taskList[i - 1] : last(taskList);
      }
    })();
    if (useHistory) {
      props.history.replace(`/tasks/detail/${get(nextItem, 'id')}${get(loca, 'search', '')}`);
    }
    setId(get(nextItem, 'id'));
  };

  const clearTag = () => {
    if (get(tag, 'assessmentId', null) !== null) {
      setDeleteConfirm({
        title: 'Remove Link?',
        content: 'This task is linked to an assessment, are you sure you want to remove the link? This task will no longer be linked to the assessment afterwards.',

        onCancel: () => setDeleteConfirm(),
        onOk: () => {
          setTag();
          setTagName();
          if (tagName === get(formState, 'name')) {
            formRef.current.updateState('name', '');
          }
          setDeleteConfirm();
        },
      });
    } else {
      setTag();
      setTagName();
      if (tagName === get(formState, 'name')) {
        formRef.current.updateState('name', '');
      }
      setDeleteConfirm();
    }
  };

  const onTagSelected = (tags) => {
    setTag(tags);
    formRef.current.updateState('tagAdded', true);
    if (!get(formState, 'name')) {
      const clname = get(tags, 'pssvChecklist.name');
      const cpname = get(tags, 'pssvCheckpoint.name');
      const name = clname + (cpname ? `: ${cpname}` : '');
      setTagName(name);
      formRef.current.updateState('name', name);
    }
  };

  return (
    <Dialog className={classes.dialog} fullScreen disableEscapeKeyDown open={true} onClose={handleClose}>
      <AppBar color="primary" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="Close" size="large">
            <CloseIcon />
          </IconButton>

          <div className={classes.barTitle}>
            <Typography variant="h5" className={classes.title}>
              {get(d, 'name', Loading)}
            </Typography>
          </div>

          <Typography variant="h6" className={classes.status}>
            <div className={classes.hideSmall}>{uppercaseFirst(status.title)}</div>
            <Icon className={classes.statusIcon}>{status.icon}</Icon>
          </Typography>
        </Toolbar>
      </AppBar>

      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {loading && (
          <div className={styles.centerColumn}>
            <Loader size={20} />
          </div>
        )}

        {!formLoading && !loading && (
          <>
            <div className={classes.buttonContainer}>
              {ate.show && (
                <Button color={'secondary'} variant="contained" disabled={ate.disabled} onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
                  {'Edit'}
                </Button>
              )}
              {canEdit && (
                <Tooltip title={ate.delete ? '' : 'Only the author or system admins can delete a task'}>
                  <span>
                    <Button color={'primary'} disabled={!ate.delete} onClick={onDelete} variant={'contained'} startIcon={<Icon>delete</Icon>} className={classes.deleteButton}>
                      {'Delete'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Confirm {...deleteConfirm} />
            </div>
          </>
        )}

        {!loading && !formLoading && !canEdit && d && (
          <div className={clsx(classes.rowOutline)}>
            <Text type="secondarySub">Created</Text>
            <div className={classes.tagContainer}>
              <Text>{get(authuser, 'name', 'Unknown')}</Text>
              <Text className={classes.createdText}>{`Created: ${moment(get(d, 'createdAt')).format('L [at] h:mma')}`}</Text>
            </div>
          </div>
        )}

        {!loading && (
          <div>
            <Form
              loading={formLoading || loading}
              showAllScreens={true}
              formStyle={classes.formStyle}
              onSubmit={formSubmit}
              onFormState={formUpdate}
              canSubmit={canEdit}
              ref={formRef}
              submitText="Save Changes"
              screens={[
                {
                  title: '',
                  titleStyle: classes.titleStyle,
                  screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                  screenContainer: {
                    props: {
                      className: clsx(classes.screenWidth, !canEdit && classes.screenWidthDisabled),
                    },
                  },
                  inputs: [
                    {
                      id: 'name',
                      type: 'TextField',
                      defaultValue: () => get(d, 'name', ''),
                      props: {
                        label: 'Title',
                        disabled: !canEdit,
                      },
                    },
                    {
                      id: 'description',
                      type: 'TextField',
                      defaultValue: () => get(d, `description`),
                      props: {
                        label: 'Description',
                        disabled: !canEdit,
                        multiline: true,
                        rows: 3,
                        rowsMax: 1000,
                        placeholder: 'N/A',
                        InputLabelProps: {
                          shrink: true,
                        },
                      },
                    },
                    {
                      id: 'assignedTo',
                      type: 'UserSelect',
                      defaultValue: () => get(d, `assignees`, []),
                      data: () => get(users, 'data', []),
                      props: {
                        multiple: true,
                        label: 'Assigned To',
                        disabled: !canEdit,
                      },
                    },
                    {
                      id: 'dueDate',
                      type: 'KeyboardDatePicker',
                      defaultValue: () => (get(d, 'dueDate') ? moment(get(d, 'dueDate')) : undefined),
                      props: {
                        label: 'Due Date',
                        disabled: !canEdit,
                        convert: (d) => {
                          return fixWeirdDateIssue(d, !formState.dueDate);
                        },
                      },
                    },
                    {
                      id: 'tradeTypes',
                      type: 'TradeSelect',
                      defaultValue: () => get(d, 'tradeTypes', []),
                      props: {
                        label: 'Trade Types',
                        checklist: get(tag, 'pssvChecklist'),
                        disabled: !canEdit,
                      },
                    },
                    canEdit && {
                      id: 'images',
                      type: 'DropArea',
                      defaultValue: () =>
                        sortBy(get(d, 'images', []), 'orderColumn').map((img) => ({
                          ...img,
                          canDelete: img.collectionName.includes('task-'),
                          tabProps: {
                            edit: {
                              disabled: !img.collectionName.includes('task-'),
                              toolTip: !img.collectionName.includes('task-') ? 'This media is a reference and can’t be edited.' : undefined,
                            },
                          },
                        })),
                      props: {
                        label: 'Images',
                        sortable: true,
                        shouldUpdateOnCaptionChange: true,
                        assessment: props.assessment,
                        uploadAllNew: true,
                        collection: 'task-images',
                        parentId: id,
                        authors: get(data, '0.authors'),
                        notAllowed: ['tag'],
                      },
                    },
                    {
                      id: 'reference',
                      props: {
                        label: 'Reference'
                      },
                      component: () => {
                        return (
                          <div>
                            <TaskScoreRef {...props} checkpoint={tag} showLink />
                            {canEdit && !get(tag, 'reportSection') && (
                              <Button
                                variant="outlined"
                                startIcon={<Icon>{tag ? 'close' : 'label'}</Icon>}
                                onClick={tag ? () => clearTag() : () => setTagModal(true)}
                                className={clsx(classes.tagButton, tag && classes.tagRemove)}
                              >
                                {tag ? 'REMOVE LINK' : 'ADD LINK'}
                              </Button>
                            )}
                          </div>
                        )
                      }
                    },
                    canEdit && {
                      id: 'meta',
                      type: 'Checkbox',
                      options: taskOptions(),
                      defaultValue: () => get(d, 'meta', {}),
                      props: {
                        label: 'Requirements',
                        disabled: !ate.isAuthOrAdmin,
                      },
                    },
                  ].filter((f) => f),
                },
              ]}
            />
          </div>
        )}

        {!loading && !formLoading && !canEdit && d && !!size(d.images) && (
          <div className={clsx(classes.imagesContainer, classes.rowOutline)}>
            <Text type="secondarySub">Images</Text>
            <div className="imagesList">
              <ImageList images={d.images} showCaption spaceBetween={5} task={d} onClose={(s) => (s.hasEdits ? update() : noop)} authors={get(d, 'authors')} />
            </div>
          </div>
        )}

        {!loading && !formLoading && d && !canEdit && <CommentContainer comments={comments} onSubmitComment={onSubmitComment} formState={formState} data={taskData} task={d} ate={ate} />}

        {size(taskList) > 1 && (
          <>
            <div className={clsx(classes.nextListItem, classes.nextListItemLeft)}>
              <IconButton onClick={() => nextListItem('left')} size="large">
                <Icon style={{ transform: 'rotate(90deg)' }}>expand_circle_down</Icon>
              </IconButton>
            </div>
            <div className={classes.nextListItem}>
              <IconButton onClick={() => nextListItem()} size="large">
                <Icon style={{ transform: 'rotate(270deg)' }}>expand_circle_down</Icon>
              </IconButton>
            </div>
          </>
        )}
      </div>
      <TaskTag open={tagModal} onTagSelected={onTagSelected} onClose={() => setTagModal(false)} />
    </Dialog>
  );
});

export default TaskDetail;
